'use client';

import {
    useMemo,
    useRef,
    useState,
    type JSX,
    type MouseEvent as ReactMouseEvent,
    type TouchEvent as ReactTouchEvent,
} from 'react';

import { curveCardinal } from '@visx/curve';
import { localPoint } from '@visx/event';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Area, LinePath } from '@visx/shape';

import useHorizontalTouchGuard from 'components/Graph/components/useHorizontalTouchGuard';
import type { KpowHistoryPoint } from 'react_pages/AmericanPowerIndex/api/kpowApi';

import { KPOW_BLUE, KPOW_RED } from '../kpowColors';
import { pointDate } from '../kpowFormat';

interface BroadcastChartProps {
    width: number;
    height: number;
    points: KpowHistoryPoint[];
    /** Fired with the hovered full-resolution point while scrubbing (null on leave). */
    onHoverPoint?: (point: KpowHistoryPoint | null) => void;
}

/**
 * Fixed visible window for the broadcast axis. Per the locked design (Figma
 * 761:3680) the axis reads +10 R .. +10 D regardless of the data; the index
 * realistically never leaves this band. NOT the +-50 methodology bound.
 */
const DOMAIN = 10;
/** Axis ticks (R positive, D negative); 0 is drawn as a dashed centerline, not labelled. */
const TICKS = [10, 5, -5, -10] as const;
/** Broadcast area-fill shades (Figma 761:5523); intentionally lighter than the KPOW_* line colors. */
const FILL_RED = '255,83,83';
const FILL_BLUE = '1,115,255';

/** Right-edge axis label metrics. Cap height is ~0.72em of Kalshi Sans; the line gap is the space
 *  between a line and its label's near edge, kept equal top and bottom (see labelBaselineY). */
const LABEL_FONT_SIZE = 20;
const LABEL_CAP_HEIGHT = LABEL_FONT_SIZE * 0.72;
const LABEL_LINE_GAP = 4;

/** Points kept for the rendered line. The scrub indexes the full-resolution `points` instead. */
const LINE_TARGET_POINTS = 200;
/** Opacity of the line/fill to the right of the scrub cursor (the "future" of the hovered point). */
const SCRUB_DIM = 0.25;

/**
 * Bucket-average downsample: split into `target` evenly-spaced buckets and average
 * each bucket's signed_value. Averaging (not stride-sampling) smooths the hourly
 * noise so the broadcast line reads as a clean trend. The latest point is preserved
 * verbatim so the right edge lands on the live value.
 */
const downsample = (points: KpowHistoryPoint[], target: number): KpowHistoryPoint[] => {
    if (points.length <= target) return points;
    const bucketSize = points.length / target;
    const out: KpowHistoryPoint[] = [];
    for (let i = 0; i < target; i += 1) {
        const bucket = points.slice(Math.floor(i * bucketSize), Math.floor((i + 1) * bucketSize));
        if (bucket.length === 0) continue;
        const avg = bucket.reduce((sum, p) => sum + p.signed_value, 0) / bucket.length;
        out.push({ ...bucket[Math.floor(bucket.length / 2)], signed_value: avg });
    }
    const last = points[points.length - 1];
    if (out.length === 0 || out[out.length - 1].end_period_ts !== last.end_period_ts) out.push(last);
    return out;
};

/**
 * One reading per calendar day (the day's last point). History is hourly, so scrubbing the raw
 * series steps through 24 points that all share a date label; collapsing to daily makes the scrub
 * advance day by day. The display line still uses the full series via downsample().
 */
const dedupeByDay = (points: KpowHistoryPoint[]): KpowHistoryPoint[] => {
    const byDay = new Map<string, KpowHistoryPoint>();
    for (const p of points) {
        const d = pointDate(p.end_period_ts);
        byDay.set(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`, p);
    }
    return Array.from(byDay.values());
};

/** Right-edge axis label, e.g. "+10 R" / "+5 D". */
const tickLabel = (value: number): string => `+${Math.abs(value)} ${value > 0 ? 'R' : 'D'}`;

/** Keep off-scale readings (the shared KPOW scale runs to +-50) inside the fixed +-10 window so the
 *  line/area/dot peg at the boundary instead of painting over the frame. The headline shows the true value. */
const clampToDomain = (value: number): number => Math.max(-DOMAIN, Math.min(DOMAIN, value));

/**
 * Broadcast-only KPOW line chart: a red-above-0 / blue-below-0 line over a fixed
 * +-10 window. The tint is a full-width zone tint (red above 0, blue below) PLUS a
 * line-bounded area fill that deepens it under the red line / above the blue line.
 * Horizontal gridlines, a dashed zero line, and colored right-edge axis labels.
 * Scrubbable: the line is low-res for a clean read, but hovering/touching resolves
 * against the full-resolution `points`, dims the line past the cursor, and reports
 * the hovered reading via onHoverPoint (the headline shows its value + date).
 */
const BroadcastChart = ({ width, height, points, onHoverPoint }: BroadcastChartProps): JSX.Element | null => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    // Touch handlers reference the latest tooltip/leave via a ref so the hook can be called
    // before the early-return guards (rules of hooks) while the handlers are defined below
    // with access to the scales.
    const tooltipRef = useRef<{
        tooltip: (e: ReactMouseEvent<SVGElement> | ReactTouchEvent<SVGElement>) => void;
        leave: () => void;
    } | null>(null);
    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useHorizontalTouchGuard<SVGElement>({
        onTouchStart: (e) => tooltipRef.current?.tooltip(e),
        onHorizontalMove: (e) => tooltipRef.current?.tooltip(e),
        onTouchEnd: () => tooltipRef.current?.leave(),
    });

    // Low-res series for the rendered line; daily series for the scrub (one reading per day).
    const linePoints = useMemo(() => downsample(points, LINE_TARGET_POINTS), [points]);
    const dailyPoints = useMemo(() => dedupeByDay(points), [points]);
    const scrubTimes = useMemo(() => dailyPoints.map((p) => pointDate(p.end_period_ts)), [dailyPoints]);

    if (points.length === 0 || width <= 0 || height <= 0) return null;

    const xScale = scaleTime<number>({
        domain: [pointDate(points[0].end_period_ts), pointDate(points[points.length - 1].end_period_ts)],
        range: [0, width],
    });
    const yScale = scaleLinear<number>({
        domain: [DOMAIN, -DOMAIN],
        range: [0, height],
    });

    const lineGradientId = `kpow-broadcast-line-${width}-${height}`;
    const zoneGradientId = `kpow-broadcast-zone-${width}-${height}`;
    const lineFillGradientId = `kpow-broadcast-fill-${width}-${height}`;
    const scrubBeforeId = `kpow-broadcast-before-${width}-${height}`;
    const scrubAfterId = `kpow-broadcast-after-${width}-${height}`;

    const midY = yScale(0);
    // Offset of the 0 line within the top->bottom gradients. Two coincident stops here
    // give a hard color break: red above 0, blue below (no purple blend).
    const zeroOffset = midY / height;
    // +5 / -5 positions on the same top->bottom gradient axis; the zone tint emanates from
    // the 0 line and is fully gone by these (so it spans only the inner +-5 band).
    const halfUpOffset = yScale(DOMAIN / 2) / height;
    const halfDownOffset = yScale(-DOMAIN / 2) / height;
    const xAccessor = (p: KpowHistoryPoint): number => xScale(pointDate(p.end_period_ts)) ?? 0;
    const yAccessor = (p: KpowHistoryPoint): number => yScale(clampToDomain(p.signed_value)) ?? 0;

    // Place each label by its line-facing edge so the gap to its line is equal everywhere (text
    // drawn on the alphabetic baseline): R labels sit a gap below their line (cap top at line+gap),
    // D labels a gap above (baseline at line-gap). This keeps top and bottom spacing symmetric.
    const labelBaselineY = (t: number): number =>
        t > 0 ? yScale(t) + LABEL_LINE_GAP + LABEL_CAP_HEIGHT : yScale(t) - LABEL_LINE_GAP;

    // The line fill uses an objectBoundingBox gradient anchored to the area's OWN extent (per
    // Figma 761:5523), so it hits full 0.35 alpha at the line's furthest excursion and fades to
    // transparent at the 0 line. fillMidOffset = where y=0 falls within that area's bounding box.
    const fillValues = linePoints.map((p) => clampToDomain(p.signed_value));
    const fillTop = yScale(Math.max(0, ...fillValues));
    const fillBottom = yScale(Math.min(0, ...fillValues));
    const fillMidOffset = fillBottom > fillTop ? (midY - fillTop) / (fillBottom - fillTop) : 0.5;

    // Scrub: map the cursor x to the nearest full-resolution point so every reading is reachable.
    const handleTooltip = (event: ReactMouseEvent<SVGElement> | ReactTouchEvent<SVGElement>): void => {
        const x = localPoint(event)?.x ?? 0;
        if (x <= 0 || x > width) {
            setActiveIndex(null);
            onHoverPoint?.(null);
            return;
        }
        const targetTime = xScale.invert(x).getTime();
        let best = 0;
        let bestDelta = Math.abs(scrubTimes[0].getTime() - targetTime);
        for (let i = 1; i < scrubTimes.length; i += 1) {
            const delta = Math.abs(scrubTimes[i].getTime() - targetTime);
            if (delta < bestDelta) {
                best = i;
                bestDelta = delta;
            }
        }
        setActiveIndex(best);
        onHoverPoint?.(dailyPoints[best]);
    };
    const handleLeave = (): void => {
        setActiveIndex(null);
        onHoverPoint?.(null);
    };
    tooltipRef.current = { tooltip: handleTooltip, leave: handleLeave };

    const activePoint = activeIndex !== null ? dailyPoints[activeIndex] : null;
    const activeX = activePoint ? (xScale(pointDate(activePoint.end_period_ts)) ?? 0) : 0;
    const activeY = activePoint ? (yScale(clampToDomain(activePoint.signed_value)) ?? 0) : 0;
    const activeColor = activePoint && activePoint.signed_value < 0 ? KPOW_BLUE : KPOW_RED;

    // Reused so the scrub split (before full / after dimmed) draws the same line.
    const line = (
        <LinePath<KpowHistoryPoint>
            data={linePoints}
            x={xAccessor}
            y={yAccessor}
            stroke={`url(#${lineGradientId})`}
            strokeWidth={5}
            curve={curveCardinal}
            fill="none"
        />
    );

    return (
        <svg
            width={width}
            height={height}
            style={{ overflow: 'visible', touchAction: 'pan-y' }}
            onMouseMove={handleTooltip}
            onMouseLeave={handleLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <defs>
                {/* Line stroke: hard split at the 0 line. Solid red above, solid blue below. */}
                <linearGradient
                    id={lineGradientId}
                    x1="0"
                    y1={yScale(DOMAIN)}
                    x2="0"
                    y2={yScale(-DOMAIN)}
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0" stopColor={KPOW_RED} />
                    <stop offset={zeroOffset} stopColor={KPOW_RED} />
                    <stop offset={zeroOffset} stopColor={KPOW_BLUE} />
                    <stop offset="1" stopColor={KPOW_BLUE} />
                </linearGradient>
                {/* Zone tint: opaque at the 0 line and fading out to transparent by +5 (red,
                    upward) and -5 (blue, downward); nothing beyond +-5. */}
                <linearGradient
                    id={zoneGradientId}
                    x1="0"
                    y1={yScale(DOMAIN)}
                    x2="0"
                    y2={yScale(-DOMAIN)}
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0" stopColor={`rgba(${FILL_RED},0)`} />
                    <stop offset={halfUpOffset} stopColor={`rgba(${FILL_RED},0)`} />
                    <stop offset={zeroOffset} stopColor={`rgba(${FILL_RED},0.17)`} />
                    <stop offset={zeroOffset} stopColor={`rgba(${FILL_BLUE},0.17)`} />
                    <stop offset={halfDownOffset} stopColor={`rgba(${FILL_BLUE},0)`} />
                    <stop offset="1" stopColor={`rgba(${FILL_BLUE},0)`} />
                </linearGradient>
                {/* Line-bounded fill (Figma 761:5523): 0.35 alpha at the line's furthest
                    excursion, fading to transparent at the 0 line. objectBoundingBox so it
                    anchors to the area's own extent; hard red/blue split at the 0 line. */}
                <linearGradient id={lineFillGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={`rgba(${FILL_RED},0.35)`} />
                    <stop offset={fillMidOffset} stopColor={`rgba(${FILL_RED},0)`} />
                    <stop offset={fillMidOffset} stopColor={`rgba(${FILL_BLUE},0)`} />
                    <stop offset="1" stopColor={`rgba(${FILL_BLUE},0.35)`} />
                </linearGradient>
                {/* Scrub split: everything up to the cursor stays full, the future side dims. */}
                {activePoint ? (
                    <>
                        <clipPath id={scrubBeforeId}>
                            <rect x={0} y={0} width={activeX} height={height} />
                        </clipPath>
                        <clipPath id={scrubAfterId}>
                            <rect x={activeX} y={0} width={Math.max(0, width - activeX)} height={height} />
                        </clipPath>
                    </>
                ) : null}
            </defs>

            {/* Background zone tint across the full width: red above 0, blue below, strongest
                at the 0 line and fading out by +-5. */}
            <rect
                x={0}
                y={yScale(DOMAIN)}
                width={width}
                height={yScale(-DOMAIN) - yScale(DOMAIN)}
                fill={`url(#${zoneGradientId})`}
            />

            {/* Plus a line-bounded area fill between the line and the 0 baseline, so the tint reads
                stronger directly under the red line / above the blue line. Dims past the cursor. */}
            <Area<KpowHistoryPoint>
                data={linePoints}
                x={xAccessor}
                y0={() => midY}
                y1={yAccessor}
                curve={curveCardinal}
            >
                {({ path }) => {
                    const d = path(linePoints) || '';
                    const fill = `url(#${lineFillGradientId})`;
                    if (!activePoint) return <path d={d} fill={fill} />;
                    return (
                        <>
                            <path d={d} fill={fill} clipPath={`url(#${scrubBeforeId})`} />
                            <path d={d} fill={fill} clipPath={`url(#${scrubAfterId})`} opacity={SCRUB_DIM} />
                        </>
                    );
                }}
            </Area>

            {TICKS.map((t) => (
                <line
                    key={t}
                    x1={0}
                    x2={width}
                    y1={yScale(t)}
                    y2={yScale(t)}
                    // +-10 boundary lines are Stroke/10 (Figma); the inner +-5 lines stay light.
                    stroke={Math.abs(t) === DOMAIN ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.08)'}
                    strokeWidth={1}
                />
            ))}
            {/* Dashed 0 baseline at Stroke/10 (Figma): rgba(0,0,0,0.9). Short dashes (not dots),
                reading as grey through the gaps. */}
            <line
                x1={0}
                x2={width}
                y1={midY}
                y2={midY}
                stroke="rgba(0,0,0,0.9)"
                strokeWidth={1}
                strokeDasharray="4,4"
            />

            {activePoint ? (
                <>
                    <g clipPath={`url(#${scrubBeforeId})`}>{line}</g>
                    <g clipPath={`url(#${scrubAfterId})`} opacity={SCRUB_DIM}>
                        {line}
                    </g>
                </>
            ) : (
                line
            )}

            {/* Scrub marker: vertical guide + a dot at the hovered reading. */}
            {activePoint ? (
                <g pointerEvents="none">
                    <line
                        x1={activeX}
                        x2={activeX}
                        y1={0}
                        y2={height}
                        stroke="rgba(0,0,0,0.35)"
                        strokeWidth={1}
                        strokeDasharray="4,4"
                    />
                    <circle cx={activeX} cy={activeY} r={6} fill="#ffffff" stroke={activeColor} strokeWidth={3} />
                </g>
            ) : null}

            {TICKS.map((t) => (
                <text
                    key={t}
                    x={width}
                    y={labelBaselineY(t)}
                    textAnchor="end"
                    dominantBaseline="alphabetic"
                    fontFamily="var(--font-kalshi-sans)"
                    fontSize={LABEL_FONT_SIZE}
                    fontWeight={600}
                    fill={t > 0 ? KPOW_RED : KPOW_BLUE}
                    stroke="#ffffff"
                    strokeWidth={3}
                    paintOrder="stroke"
                    style={{ fontVariantNumeric: 'lining-nums tabular-nums' }}
                >
                    {tickLabel(t)}
                </text>
            ))}
        </svg>
    );
};

export default BroadcastChart;
